package org.teddapp.project.ui

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.runtime.Composable
import androidx.compose.material3.ColorScheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.material3.ProvideTextStyle
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.layout.imePadding

/**
 * Palette TEDD : les tokens de couleur du design system, dans les deux thèmes.
 *
 * Neutres chauds partout ; le citron vert ([lime]) signale ce qui est actif dans le
 * privé, la latérite ([laterite]) ce qui est public. Chaque couple texte/fond nommé
 * ici atteint 4.5:1 dans les deux thèmes.
 *
 * Trois règles à garder en tête en écrivant un écran :
 * - [lime] est un remplissage, toujours derrière [onLime]. Pour du texte, une icône
 *   ou un tracé dans la teinte de marque, c'est [limeStrong].
 * - [line] sépare (décoratif) ; [lineStrong] contourne un contrôle.
 * - [inkSubtle] ne porte jamais une information essentielle (3:1 seulement).
 */
data class TeddColors(
    /** Vrai pour la palette sombre : les ombres y sont remplacées par un liseré. */
    val isDark: Boolean,

    // ── Surfaces ───────────────────────────────────────────────────────
    /** Fond de page, le « papier ». */
    val bg: Color,
    /** Cartes, tuiles, champs, lignes de liste. */
    val surface: Color,
    /** Zones en retrait : barre latérale, piste des barres de progression. */
    val surfaceSunken: Color,
    /** Calques flottants : palette, menus, popovers. Toujours avec l'ombre de calque. */
    val surfaceOverlay: Color,
    /** Filet décoratif : séparateurs, contour des cartes. */
    val line: Color,
    /** Bordure d'un contrôle (champ, case à cocher). */
    val lineStrong: Color,

    // ── Texte ──────────────────────────────────────────────────────────
    /** Texte principal. */
    val ink: Color,
    /** Texte secondaire, métadonnées, libellés. */
    val inkMuted: Color,
    /** Placeholders, désactivé, icônes décoratives. */
    val inkSubtle: Color,

    // ── Action ─────────────────────────────────────────────────────────
    /** Remplissage du bouton principal : encre en clair, citron vert en sombre. */
    val action: Color,
    val onAction: Color,
    val actionHover: Color,

    // ── Citron vert : ce qui est actif ─────────────────────────────────
    val lime: Color,
    val onLime: Color,
    val limeStrong: Color,
    val limeSoft: Color,

    // ── Latérite : ce qui est public ───────────────────────────────────
    val laterite: Color,
    val onLaterite: Color,
    val lateriteSoft: Color,

    // ── Signaux : toujours accompagnés d'une icône, d'un signe ou d'un mot ──
    /** Succès, « fait », solde positif. Bleu lagune, hors de l'axe rouge–vert. */
    val success: Color,
    val successSoft: Color,
    val warning: Color,
    val warningSoft: Color,
    val danger: Color,
    val dangerSoft: Color,
    val onDanger: Color,

    // ── Repères de module : icône et pastille, jamais un fond ni un titre ──
    val modToday: Color,
    val modFocus: Color,
    val modPortfolio: Color,
    val modGoals: Color,
    val modNotes: Color,
    val modHabits: Color,
    val modFinance: Color,
    val modInbox: Color,

    // ── Paliers de compétence (propres à l'application) ────────────────
    val bronze: Color,
    val argent: Color,
    val or: Color,
) {
    /** Anneau de focus clavier : 2 px plein, décalé de 2 px. */
    val focusRing: Color get() = limeStrong
    /** Liens dans le texte courant. */
    val link: Color get() = laterite
}

val LightTeddColors = TeddColors(
    isDark = false,
    bg = Color(0xFFF5F2EC),
    surface = Color(0xFFFFFFFF),
    surfaceSunken = Color(0xFFECE8DF),
    surfaceOverlay = Color(0xFFFFFFFF),
    line = Color(0xFFE3DED4),
    lineStrong = Color(0xFF837B6E),
    ink = Color(0xFF1A1916),
    inkMuted = Color(0xFF5E584F),
    inkSubtle = Color(0xFF837B6E),
    action = Color(0xFF1A1916),
    onAction = Color(0xFFF5F2EC),
    actionHover = Color(0xFF3A3833),
    lime = Color(0xFFC8F051),
    onLime = Color(0xFF1A1916),
    limeStrong = Color(0xFF4A7309),
    limeSoft = Color(0xFFEBF7C6),
    laterite = Color(0xFFB53B0A),
    onLaterite = Color(0xFFFFFFFF),
    lateriteSoft = Color(0xFFFBE4D8),
    success = Color(0xFF0D6C74),
    successSoft = Color(0xFFDCEFF0),
    warning = Color(0xFF8F5500),
    warningSoft = Color(0xFFFBEBD0),
    danger = Color(0xFFC3281E),
    dangerSoft = Color(0xFFFBE0DC),
    onDanger = Color(0xFFFFFFFF),
    modToday = Color(0xFF4A7309),
    modFocus = Color(0xFF4A4FC4),
    modPortfolio = Color(0xFFA3366F),
    modGoals = Color(0xFF0D6C74),
    modNotes = Color(0xFF8F5500),
    modHabits = Color(0xFF2A7335),
    modFinance = Color(0xFF1F5FA8),
    modInbox = Color(0xFF5E584F),
    bronze = Color(0xFF8A5A2B),
    argent = Color(0xFF5E6873),
    or = Color(0xFF8F6A00),
)

val DarkTeddColors = TeddColors(
    isDark = true,
    bg = Color(0xFF12110F),
    surface = Color(0xFF1B1A17),
    surfaceSunken = Color(0xFF0B0A09),
    surfaceOverlay = Color(0xFF24221F),
    line = Color(0xFF2E2B27),
    lineStrong = Color(0xFF736C61),
    ink = Color(0xFFF2EEE7),
    inkMuted = Color(0xFFABA498),
    inkSubtle = Color(0xFF7D766B),
    action = Color(0xFFC8F051),
    onAction = Color(0xFF12110F),
    actionHover = Color(0xFFD8F77F),
    lime = Color(0xFFC8F051),
    onLime = Color(0xFF12110F),
    limeStrong = Color(0xFFC8F051),
    limeSoft = Color(0xFF222A10),
    laterite = Color(0xFFFF8A5B),
    onLaterite = Color(0xFF12110F),
    lateriteSoft = Color(0xFF3A1A0C),
    success = Color(0xFF5FD0D8),
    successSoft = Color(0xFF0E2C2E),
    warning = Color(0xFFF2B34C),
    warningSoft = Color(0xFF33240A),
    danger = Color(0xFFFF7A6B),
    dangerSoft = Color(0xFF3A1513),
    onDanger = Color(0xFF12110F),
    modToday = Color(0xFFC8F051),
    modFocus = Color(0xFFA8ACFF),
    modPortfolio = Color(0xFFF08BC0),
    modGoals = Color(0xFF5FD0D8),
    modNotes = Color(0xFFF2B34C),
    modHabits = Color(0xFF7ED68B),
    modFinance = Color(0xFF80B5FF),
    modInbox = Color(0xFFABA498),
    bronze = Color(0xFFD9A066),
    argent = Color(0xFFB4BFCA),
    or = Color(0xFFE6BE5A),
)

/** CompositionLocal qui porte la palette courante. Static car ne change pas souvent. */
val LocalTeddColors = staticCompositionLocalOf { LightTeddColors }

enum class ThemeMode { Light, Dark, System }

/**
 * Wrapper de thème TEDD : décide light/dark en fonction du mode + isSystemInDarkTheme().
 * Wrap les entry points de chaque app cible.
 */
@Composable
fun TeddTheme(
    themeMode: ThemeMode = ThemeMode.System,
    content: @Composable () -> Unit,
) {
    val systemDark = isSystemInDarkTheme()
    val isDark = when (themeMode) {
        ThemeMode.Light -> false
        ThemeMode.Dark -> true
        ThemeMode.System -> systemDark
    }
    val colors = if (isDark) DarkTeddColors else LightTeddColors
    val fonts = rememberTeddFonts()
    CompositionLocalProvider(
        LocalTeddColors provides colors,
        LocalTeddFonts provides fonts,
    ) {
        // Les composants Material — menus déroulants en tête — tirent leurs surfaces
        // de la palette Material, pas de la nôtre. Sans ce pont, un menu s'ouvrait
        // blanc dans une application sombre, et son texte, lui, prenait notre couleur
        // claire : illisible, et dans tous les écrans à la fois.
        MaterialTheme(colorScheme = colors.toMaterialScheme()) {
            // Le clavier prend sa place sous l'application au lieu de la recouvrir : le champ
            // qu'on remplit reste visible. Sans effet là où il n'y a pas de clavier à l'écran.
            ProvideTextStyle(TeddType.Body.copy(color = colors.ink)) {
                androidx.compose.foundation.layout.Box(
                    modifier = androidx.compose.ui.Modifier.imePadding(),
                    propagateMinConstraints = true,
                ) { content() }
            }
        }
    }
}

/**
 * Traduit la palette vers le schéma Material.
 *
 * On ne mappe que ce qui sert : les surfaces, le texte dessus, et l'action. Le reste
 * hérite des valeurs par défaut du mode correspondant — un composant Material qu'on
 * n'utilise pas n'a pas besoin d'une couleur choisie.
 */
private fun TeddColors.toMaterialScheme(): ColorScheme {
    val base = if (isDark) darkColorScheme() else lightColorScheme()
    return base.copy(
        primary = action,
        onPrimary = onAction,
        primaryContainer = limeSoft,
        onPrimaryContainer = ink,
        secondary = limeStrong,
        onSecondary = if (isDark) onLime else Color.White,
        background = bg,
        onBackground = ink,
        surface = surface,
        onSurface = ink,
        surfaceVariant = surfaceSunken,
        onSurfaceVariant = inkMuted,
        // Les menus sont des calques flottants : ils se posent sur surfaceOverlay.
        surfaceContainer = surfaceOverlay,
        surfaceContainerHigh = surfaceOverlay,
        surfaceContainerHighest = surfaceSunken,
        outline = lineStrong,
        outlineVariant = line,
        error = danger,
        onError = onDanger,
    )
}
